import React, { FunctionComponent, ReactNode, useCallback, useMemo, useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { MenuItem } from "@mui/material";
import { useFlags } from "../../services/utilities/launch_darkly_helper";

import "../../../css/nav_bar/nav_bar_panels_menu.css";
import CheckboxMenuItem from "../general/checkbox_menu_item";
import getPageName from "../../services/getters/location_metadata/get_page_name";
import getProjectId from "../../services/getters/project_getters/get_project_id";
import getVisiblePanels from "../../services/getters/selection_getters/get_visible_panels";
import NavBarDropdown from "./nav_bar_dropdown";
import panelHidden from "../../events/selection/panels/panel_hidden";
import panelNodesChanged from "../../events/selection/panels/panel_nodes_changed";
import panelShown from "../../events/selection/panels/panel_shown";
import panelsReset from "../../events/selection/panels/panels_reset";
import useIsMobileView from "../../services/component_helpers/component_effects/use_is_mobile_view";
import usePopover from "../../services/component_helpers/component_effects/use_popover";
import useProject from "../../queries/projects/use_project";
import { getEnabledPanelTypesByPageAndFlags, getPanelDisplayName, getPanelNodesByPanelType, PanelType } from "../../models/domain/enums/panel_type";
import pageNames from "../../models/domain/enums/page_names";

// Overridable so the second-level nav can use the chevron its design calls for; leaving it unset
// falls through to NavBarDropdown's own, keeping the legacy nav unchanged.
export type OwnProps = { endIcon?: ReactNode };

export type Props = OwnProps & ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;

export const NavBarPanelsMenu: FunctionComponent<Props> = (props) => {
  const { endIcon, pageName, visiblePanels, panelHidden, panelShown, projectId, panelsReset, isFirstTimeRendered } = props;
  const {
    enableNativeReportCharts,
    projectSummaryPageBonusPanels,
    elementInfoPanel,
  } = useFlags();
  const { data: project } = useProject(projectId);
  const [openState, anchorEl, handleOpen, onClose] = usePopover();
  const photosOnly = project?.settings?.photosOnly;
  const progressReportTab = project?.settings?.progress4d;
  const showCustomReportsOnSummary = project?.projectReports?.filter(report => report.type !== "INSPECT").length > 0;
  const showProgressOnSummary = project?.settings?.progress4d;
  const isUserOnMobile = useIsMobileView();

  const panelTypes = useMemo(() => {
    return getEnabledPanelTypesByPageAndFlags(pageName, {
      groupsPanel: true,
      customReportsPanel: true,
      photosOnly,
      projectAreasModelViewerPanel: projectSummaryPageBonusPanels,
      projectAreaSummaryPanel: projectSummaryPageBonusPanels,
      desktopOnly: !isUserOnMobile,
      progressReportTab,
      showProgressOnSummary,
      showCustomReportsOnSummary,
      enableNativeReportCharts
    });
  }, [pageName, photosOnly, isUserOnMobile, progressReportTab, showProgressOnSummary, showCustomReportsOnSummary, enableNativeReportCharts, projectSummaryPageBonusPanels]);

  const onReset = useCallback(() => {
    panelsReset(pageName);
    if (window.Avvir?.resetPanels) {
      window.Avvir.resetPanels();
    }
    onClose();
  }, [onClose, pageName, panelsReset]);


  const panelNodes = useMemo(() => {
    return panelTypes
      ?.map((panelType) => ({
        ...getPanelNodesByPanelType(panelType),
        name: getPanelDisplayName(panelType, { elementInfoPanel })
      }))
      ?.sort((a, b) => a.name.localeCompare(b.name));
  }, [panelTypes, elementInfoPanel]);

  const hideAllPanelsExceptReportsOnInitialRenderSummaryPage = useCallback(() => {
    panelNodes?.forEach((panel) => {
      if (panel.component !== PanelType.CUSTOM_REPORTS_PANEL) {
        panelHidden(pageName, panel.component);
      }
    });
  }, [panelNodes, pageName]);

  const [isInitialRender, setIsInitialRender] = useState(isFirstTimeRendered);

  const panelOptionsMarkup = useMemo(() => {
    if (isInitialRender && pageName === pageNames.PROJECT_SUMMARY) {
      hideAllPanelsExceptReportsOnInitialRenderSummaryPage();
    }
    return panelNodes
      .map((node) => {
        const panelType = node.component;
        return <CheckboxMenuItem<PanelType>
          key={"panel-" + panelType}
          value={panelType}
          enabled={visiblePanels.includes(panelType)}
          onItemEnable={(value) => {
            setIsInitialRender(false);
            panelShown(pageName, value);
          }}
          onItemDisable={(value) => {
            setIsInitialRender(false);
            panelHidden(pageName, value);
          }}
          label={node.name}
        />;
      });
  }, [pageName, panelHidden, panelShown, panelNodes, visiblePanels, isInitialRender]);

  return <NavBarDropdown
    className="NavBarPanelsMenu-button"
    label="Panels"
    popoverProps={{
      open: openState,
      anchorEl,
      handleOpen,
      onClose
    }}
    slotProps={{
      ...endIcon && { button: { endIcon } },
      menu: {
        className: "NavBarPanelsMenu"
      }
    }}
  >
    {panelOptionsMarkup}
    <MenuItem key="panels-reset" onClick={onReset}>
      Reset Panels to Default Layout
    </MenuItem>
  </NavBarDropdown>;
};

const isFirstTimeRendered = (state) => {
  let isFirstTimeRendered: boolean = null;

  if (state.location.prev.type === "") {
    isFirstTimeRendered = false;
  }
  else if (state.location.type !== state.location.prev.type) {
    isFirstTimeRendered = true;
  }
  return isFirstTimeRendered;
}

const mapStateToProps = (state, props) => ({
  visiblePanels: getVisiblePanels(state, props),
  pageName: getPageName(state, props),
  projectId: getProjectId(state, props),
  isFirstTimeRendered: isFirstTimeRendered(state)
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  panelNodesChanged,
  panelHidden,
  panelShown,
  panelsReset
}, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(NavBarPanelsMenu);
